<template>
  <header 
    class="main-header" 
    :style="{ backgroundColor: headerData.bgColor, '--hover-color': headerData.hoverColor }"
  >
    <div class="header-container">
      <div class="logo-section">
        <div class="logo-icon" :style="{ backgroundColor: headerData.bgColor }">
          <img v-if="headerData.imageUrl" :src="headerData.imageUrl" class="header-logo-img">
          <span v-else class="placeholder-text">FP</span>
        </div>
        
        <div 
          class="logo-text-rich" 
          v-html="headerData.brandText"
          :style="{ color: headerData.textColor }"
        ></div>
      </div>
      <button class="menu-toggle" @click="toggleMenu" aria-label="Abrir Menu">
        <span :class="{ 'open': isMenuOpen }"></span>
        <span :class="{ 'open': isMenuOpen }"></span>
        <span :class="{ 'open': isMenuOpen }"></span>
      </button>
      <nav class="nav-menu" :class="{ 'is-active': isMenuOpen }">
        <router-link :to="{ path: '/', hash: '#home' }"class="nav-link" @click="setActiveMenu('home')">
          <span :style="getManualStyle('home')">Home</span>
        </router-link>
        <router-link :to="{ path: '/ebooks'}" class="nav-link" @click="setActiveMenu('Livros')">
          <span :style="getManualStyle('Livros')">Livros</span>
        </router-link>
        <router-link :to="{ path: '/', hash: '#information' }" class="nav-link" @click="setActiveMenu('Sobre')">
          <span :style="getManualStyle('Sobre')">Sobre</span>
        </router-link>
        <router-link :to="{ path: '/', hash: '#contato' }" class="nav-link" @click="setActiveMenu('Contato')">
          <span :style="getManualStyle('Contato')">Contato</span>
        </router-link>
      </nav>
    </div>
  </header>
</template>

<script setup lang="ts">
  import { ref, watch, onMounted } from 'vue';
  
  const endpoint = process.env.VUE_APP_HYGRAPH_URL;
  const activeMenu = ref('home')

  const setActiveMenu = (menuName : string) =>{
    activeMenu.value = menuName;
    isMenuOpen.value = false;
  }
  
  const hoverItem = ref<string | null>(null);

  const getManualStyle = (name: string) => {
    const isActive = activeMenu.value === name;
    const isHovered = hoverItem.value === name;
    
    const showDestaque = isActive || isHovered;

    return {
      color: showDestaque ? headerData.value.hoverColor : headerData.value.textColor,
      borderBottom: `2px solid ${showDestaque ? headerData.value.hoverColor : 'transparent'}`,
      paddingBottom: '5px',
      transition: 'all 0.3s ease',
      fontWeight: '500',
      cursor: 'pointer'
    };
  };
  
  const headerData = ref({
    brandText: '', 
    imageUrl: '',
    bgColor: '#0E2038',
    textColor: '#ffffff',
    hoverColor: '#9ACB56'
  });

  const fetchHeaderConfig = async () => {
    try {      
      const query = `{
        headers {
          brandText
          bgColor { hex }
          textColor { hex }
          hoverColor { hex }
          image { url }
        }
      }`;

      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query })
      });
      
      const { data } = await res.json();
      if (data?.headers?.[0]) {
        const h = data.headers[0];
        // Atualizando o estado reativo
        headerData.value = {
          brandText: h.brandText || '',
          imageUrl: h.image?.url || '',
          bgColor: h.bgColor?.hex || '#0E2038',
          textColor: h.textColor?.hex || '#ffffff',
          hoverColor: h.hoverColor?.hex || '#9ACB56'
        };
      }
    } catch (error) {
      console.error("Erro ao carregar configurações:", error);
    }
  };

  // 1. Estado para controlar se o menu está aberto ou não
const isMenuOpen = ref(false);

// 2. Função para alternar o estado (abre/fecha)
const toggleMenu = () => {
  isMenuOpen.value = !isMenuOpen.value;
};

  // 3. Função para fechar o menu (útil quando clicar em um link)
  const closeMenu = () => {
    isMenuOpen.value = false;
  };

  // 4. Bloqueia o scroll da página quando o menu estiver aberto
  // Isso evita que o usuário role o fundo do site enquanto navega no menu
  watch(isMenuOpen, (isOpen) => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
  });


  onMounted(fetchHeaderConfig);
</script>

<style scoped>
  .main-header {
    padding: 15px 0;
    position: sticky;
    top: 0;
    z-index: 1000;
    border-bottom: 1px solid rgba(255,255,255,0.1);
  }

  .header-container {
    max-width: 1200px;
    margin: 0 auto;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 20px;
  }

  /* Lado Esquerdo: Logo e Texto Lado a Lado */
  .logo-section {
    display: flex;
    align-items: center; /* Garante que o texto fique centralizado com o círculo */
    gap: 15px; /* Espaço entre o círculo e o texto */
  }

  .logo-icon {
    width: 60px;
    height: 60px;
    min-width: 60px;
    border-radius: 50%;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .header-logo-img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    padding: 1px;
  }

  /* Texto do Quill: Respeita quebras de linha do editor */
  .logo-text-rich {
    white-space: pre-wrap; /* Crucial para respeitar as quebras de linha do Hygraph */
  }

  .logo-text-rich :deep(p) {
    margin: 0;
    line-height: 1.2;
  }
  /* Nav Menu */
  .nav-menu {
    display: flex;
    gap: 25px;
  }

  .nav-link {
    color: #ffffff;
    text-decoration: none;
    font-size: 0.95rem;
    font-weight: 400;
    transition: color 0.3s;
  }

  .nav-link.active {
    color: #9ACB56;
    border-bottom: 2px solid #9ACB56;
    padding-bottom: 4px;
  }

  .nav-link:hover {
    color: #9ACB56;
  }

  /* Actions Section */
  .actions {
    display: flex;
    align-items: center;
    gap: 20px;
  }
  .menu-toggle {
  display: none;
  flex-direction: column;
  gap: 6px;
  background: none;
  border: none;
  cursor: pointer;
  z-index: 1100;
}

.menu-toggle span {
  width: 30px;
  height: 3px;
  background-color: #fff;
  transition: 0.3s;
  border-radius: 2px;
}

/* Animação do X quando aberto */
.menu-toggle span.open:nth-child(1) { transform: translateY(9px) rotate(45deg); }
.menu-toggle span.open:nth-child(2) { opacity: 0; }
.menu-toggle span.open:nth-child(3) { transform: translateY(-9px) rotate(-45deg); }

/* --- RESPONSIVIDADE --- */

@media (max-width: 992px) {
  .menu-toggle {
    display: flex; /* Mostra o botão no mobile/tablet */
  }

  .nav-menu {
    position: fixed;
    top: 0;
    right: -100%; /* Escondido fora da tela */
    width: 250px;
    height: 100vh;
    background-color: #0d1117; /* Cor escura sólida */
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 40px;
    transition: 0.4s ease-in-out;
    z-index: 1050;
    box-shadow: -5px 0 15px rgba(0,0,0,0.5);
  }

  .nav-menu.is-active {
    right: 0; /* Desliza para dentro da tela */
  }

  .nav-link {
    font-size: 1.2rem; /* Links maiores para toque fácil */
  }

  /* Ajuste da Logo para não apertar com o botão */
  .logo-icon {
    width: 50px;
    height: 50px;
    min-width: 50px;
  }

  .logo-text-rich {
    font-size: 0.85rem;
  }
}

/* Ajuste fino para telas muito pequenas */
@media (max-width: 480px) {
  .logo-text-rich {
    display: none; /* Esconde o texto da logo se ficar muito apertado no celular */
  }
}
</style>